import config from "../clientConfig";

/** Basketmouth / MITO brand orange — exact hex used on basketmouth.money. */
export const MITO_ORANGE = "#FB4B14";
export const MITO_ORANGE_HOVER = "#E04312";

/** @deprecated Use MITO_ORANGE — same value. */
export const MITO_RED = MITO_ORANGE;
/** @deprecated Use MITO_ORANGE_HOVER — same value. */
export const MITO_RED_HOVER = MITO_ORANGE_HOVER;

export const TRUST_AFFILIATE_ID = "cea8cf3e-701a-45e8-b9e9-9be283229f87";

const NON_TENANT_MITO_HOSTS = new Set([
  "mito.money",
  "www.mito.money",
  "widget.mito.money",
  "staging.mito.money",
  "admin.mito.money",
  "staging-admin.mito.money",
  "dev-admin.mito.money",
]);

const BASKETMOUTH_HOSTS = new Set([
  "basketmouth.money",
  "www.basketmouth.money",
  "basketmouth.mito.money",
]);

export function isBasketmouthHost(hostname?: string): boolean {
  if (typeof window === "undefined" && !hostname) return false;
  const host = (
    hostname ||
    (typeof window !== "undefined" ? window.location.hostname : "")
  ).toLowerCase();
  return BASKETMOUTH_HOSTS.has(host);
}

/** *.mito.money tenant hosts (trust, future {client}.mito.money) — not widget/admin. */
export function isMitoTenantHost(hostname?: string): boolean {
  if (process.env.REACT_APP_MITO_BRAND === "1") return true;
  if (config.isSubdomain) return true;
  if (typeof window === "undefined" && !hostname) return false;
  const host = (
    hostname ||
    (typeof window !== "undefined" ? window.location.hostname : "")
  ).toLowerCase();
  if (!host.endsWith(".mito.money")) return false;
  return !NON_TENANT_MITO_HOSTS.has(host);
}

function affiliateIdFrom(configOrId?: any): string {
  if (!configOrId) return "";
  if (typeof configOrId === "string") return configOrId.trim().toLowerCase();
  const raw =
    configOrId.affiliateId ||
    configOrId.AffiliateId ||
    configOrId.affiliateid ||
    "";
  return String(raw).trim().toLowerCase();
}

/** Every MITO widget surface uses orange brand tokens (#FB4B14), not affiliate API colours. */
export function shouldUseMitoOrangeTheme(_affiliateConfig?: any): boolean {
  return true;
}

/**
 * Host / affiliate helper historically named “MITO red”.
 * Same boolean logic as before — not a theme colour gate for product features.
 */
export function shouldUseMitoOrange(affiliateConfig?: any): boolean {
  if (process.env.REACT_APP_MITO_BRAND === "1") return true;
  if (isLocalDevHost()) return true;
  if (isMitoTenantHost()) return true;
  if (isBasketmouthHost()) return true;
  const stored =
    typeof window !== "undefined"
      ? (localStorage.getItem("affiliateId") || "").toLowerCase()
      : "";
  const id = affiliateIdFrom(affiliateConfig) || stored;
  return id === TRUST_AFFILIATE_ID;
}

/** @deprecated Use shouldUseMitoOrange — same logic. */
export const shouldUseMitoRed = shouldUseMitoOrange;

export function resolvePrimaryColour(affiliateConfig?: any): string {
  if (shouldUseMitoOrangeTheme(affiliateConfig)) return MITO_ORANGE;
  return (
    affiliateConfig?.primaryColourCode ||
    affiliateConfig?.primaryColorCode ||
    affiliateConfig?.PrimaryColourCode ||
    "#000"
  );
}

export function resolveNavBarColour(affiliateConfig?: any): string {
  if (shouldUseMitoOrangeTheme(affiliateConfig)) return MITO_ORANGE;
  return affiliateConfig?.defaultColourCode || resolvePrimaryColour(affiliateConfig);
}

/** Force Basketmouth / MITO brand orange on mito subdomain tenants. */
export function applyMitoBrandToConfig(affiliateConfig?: any): any {
  if (!affiliateConfig || typeof affiliateConfig !== "object") {
    return affiliateConfig;
  }
  if (!shouldUseMitoOrangeTheme(affiliateConfig)) {
    return affiliateConfig;
  }
  return {
    ...affiliateConfig,
    primaryColourCode: MITO_ORANGE,
    primaryColorCode: MITO_ORANGE,
    PrimaryColourCode: MITO_ORANGE,
    defaultColourCode: MITO_ORANGE,
    secondaryColourCode: MITO_ORANGE,
    affiliateFavicon: null,
  };
}

export const TRUST_PREVIEW_ORIGIN = "https://trust.mito.money";

export function isLocalDevHost(hostname?: string): boolean {
  const host = (
    hostname ||
    (typeof window !== "undefined" ? window.location.hostname : "")
  ).toLowerCase();
  return host === "localhost" || host === "127.0.0.1";
}

if (typeof document !== "undefined" && shouldUseMitoOrangeTheme()) {
  document.documentElement.classList.add("mito-brand", "mito-theme-orange");
}

/** Origin the API should bind for local Trust preview. */
export function resolveAffiliateDomainHeader(): string | undefined {
  if (typeof window === "undefined") return undefined;
  if (isLocalDevHost() || process.env.REACT_APP_MITO_BRAND === "1") {
    return TRUST_PREVIEW_ORIGIN;
  }
  if (window.location?.origin) return window.location.origin;
  return undefined;
}

function flagFromConfig(cfg: any, camel: string, pascal: string): boolean | undefined {
  if (!cfg || typeof cfg !== "object") return undefined;
  const lower = camel.toLowerCase();
  if (camel in cfg) return !!cfg[camel];
  if (pascal in cfg) return !!cfg[pascal];
  if (lower in cfg) return !!cfg[lower];
  return undefined;
}

/** Send Money + Recipients — AffiliateAllowedService.IsMoneyTransfer only. */
export function affiliateAllowsSendMoney(affiliateConfig?: any): boolean {
  return flagFromConfig(affiliateConfig, "isMoneyTransfer", "IsMoneyTransfer") === true;
}

/**
 * Receive Money + Senders — AffiliateAllowedService.IsReceiveMoney only (not theme).
 * If the API has not shipped `isReceiveMoney` yet, fall back to Send so tenants
 * do not lose Receive during the staged rollout.
 */
export function affiliateAllowsReceiveMoney(affiliateConfig?: any): boolean {
  const explicit = flagFromConfig(
    affiliateConfig,
    "isReceiveMoney",
    "IsReceiveMoney",
  );
  if (explicit !== undefined) return explicit;
  return affiliateAllowsSendMoney(affiliateConfig);
}
